/**
 * A gaveta lateral do Lumma Store — spec 01, "Drawer".
 *
 * Existia em `modulos/financeiro/`, e por morar dentro de um módulo nenhuma
 * outra tela a enxergava: clientes, fornecedores, produtos e notas fiscais
 * escreveram a sua própria. Eram SEIS molduras, com quatro larguras diferentes
 * (480, 520, 560 e 760) e — o que importa de verdade — nenhuma delas prendendo
 * o foco.
 *
 * ## O que `aria-modal` promete e o código precisa cumprir
 *
 * As seis versões anteriores declaravam `role="dialog" aria-modal="true"` e
 * paravam aí. Esse atributo é uma AFIRMAÇÃO ao leitor de tela: "o resto da
 * página não existe agora". Nenhuma delas movia o foco para dentro, prendia o
 * Tab ou devolvia o foco ao fechar — então abrir a ficha de um cliente e apertar
 * Tab continuava percorrendo a tabela ATRÁS da gaveta, com o leitor de tela
 * anunciando linhas que o olho não vê e o mouse não alcança.
 *
 * Esta moldura cumpre as três partes:
 *
 * 1. **Foco entra.** No primeiro elemento focável, ou na própria gaveta quando
 *    ela é só leitura.
 * 2. **Foco fica.** Tab no último volta ao primeiro; Shift+Tab no primeiro vai
 *    ao último.
 * 3. **Foco volta.** Ao fechar, retorna para o elemento que a abriu — senão o
 *    teclado reinicia no topo da página, e quem estava na linha 40 da tabela
 *    recomeça do começo.
 *
 * ## Sair sem querer
 *
 * `aoFechar` é o caminho normal. `confirmarSaida` existe para a gaveta que
 * contém FORMULÁRIO: sem ela, Esc ou um clique no fundo descartam o que foi
 * digitado, sem aviso e sem desfazer. Quem tem rascunho passa uma função que
 * pergunta antes.
 */
import { useCallback, useRef, type ReactNode } from 'react'

import { BotaoIcone } from './Botao'
import { usarEsc, usarFocoPreso } from './foco'

/** As larguras da spec 01. `larga` é o desvio registrado em docs/de-para.md. */
export type LarguraGaveta = 'padrao' | 'tabela' | 'formulario' | 'larga'

const LARGURA: Record<LarguraGaveta, string> = {
  /** 480px — a ficha de leitura. O valor da spec. */
  padrao: 'w-[480px]',
  /** 520px — quando tem tabela dentro. Também da spec. */
  tabela: 'w-[520px]',
  /**
   * 560px — formulário com pares de campo lado a lado. Em 520 os pares quebram
   * em coluna única e o formulário fica com o dobro da altura.
   */
  formulario: 'w-[560px]',
  /** 760px — a ficha de nota fiscal, que carrega a tabela de itens completa. */
  larga: 'w-[760px]',
}

export function Gaveta({
  rotulo,
  largura = 'padrao',
  aoFechar,
  confirmarSaida,
  children,
}: {
  /** Nome acessível do diálogo — é como o leitor de tela anuncia a gaveta. */
  rotulo: string
  largura?: LarguraGaveta
  aoFechar: () => void
  /**
   * Chamada antes de fechar por Esc ou clique no fundo. Retornar `false`
   * cancela o fechamento. Use quando houver rascunho a perder.
   */
  confirmarSaida?: () => boolean
  children: ReactNode
}) {
  const painel = useRef<HTMLElement>(null)

  const tentarFechar = useCallback(() => {
    if (confirmarSaida && !confirmarSaida()) return
    aoFechar()
  }, [aoFechar, confirmarSaida])

  // O painel leva `tabIndex={-1}` para poder receber o foco quando não há
  // controle nenhum dentro — uma ficha só de leitura é o caso.
  usarFocoPreso(painel)
  usarEsc(tentarFechar)

  return (
    <>
      {/*
        A camada de fundo é um `<button>` para o clique-fora funcionar sem
        `onClick` numa `<div>`, mas sai do ciclo do Tab com `tabIndex={-1}` e
        do leitor de tela com `aria-hidden`: quem navega por teclado já tem o
        Esc, e um "botão fechar" invisível ocupando a tela inteira seria ruído.
      */}
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        // Gancho para o teste alcançar o fundo. Ele não pode ser encontrado por
        // papel e nome — é `aria-hidden` de propósito —, e "fecha ao clicar no
        // fundo" é comportamento que merece teste.
        data-fundo-da-gaveta
        onClick={tentarFechar}
        className="fixed inset-0 z-40 cursor-default bg-[rgba(15,23,42,0.40)]"
      />
      <aside
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-label={rotulo}
        tabIndex={-1}
        className={`fixed top-0 right-0 bottom-0 z-50 flex max-w-full flex-col bg-superficie shadow-[-12px_0_40px_rgba(15,23,42,0.18)] ${LARGURA[largura]}`}
      >
        {children}
      </aside>
    </>
  )
}

/**
 * O cabeçalho de 56px da spec 01 — título à esquerda, X à direita.
 *
 * Está aqui, e não em cada tela, porque a altura fixa do cabeçalho é o que
 * permite ao corpo rolar sozinho: `CorpoDaGaveta` usa `flex-1` contra este
 * `shrink-0`. Com o cabeçalho rolando junto, a pessoa perde a referência de
 * qual registro está vendo assim que desce dois blocos.
 */
export function CabecalhoDaGaveta({
  titulo,
  subtitulo,
  aoFechar,
  rotuloFechar = 'Fechar',
  children,
}: {
  titulo: ReactNode
  subtitulo?: ReactNode
  aoFechar: () => void
  /**
   * Nome acessível do X. Vale a pena ser específico ("Fechar ficha") quando o
   * rodapé também tem um "Fechar": dois botões com o MESMO nome acessível na
   * mesma caixa deixam quem usa leitor de tela sem saber qual é qual — e
   * quebram qualquer busca por papel e nome, inclusive a dos testes.
   */
  rotuloFechar?: string
  /** Conteúdo extra à direita, antes do X — um selo de situação, por exemplo. */
  children?: ReactNode
}) {
  return (
    <header className="flex min-h-[56px] shrink-0 items-center gap-3 border-b border-borda px-5 py-3">
      <div className="min-w-0 flex-1">
        <h2 className="truncate font-titulo text-[16px] font-bold text-titulo">{titulo}</h2>
        {subtitulo && <p className="mt-0.5 truncate text-[12.5px] text-texto-suave">{subtitulo}</p>}
      </div>
      {children}
      <BotaoIcone icone="close" rotulo={rotuloFechar} tom="suave" onClick={aoFechar} />
    </header>
  )
}

/** O corpo rolável, entre o cabeçalho e o rodapé fixos. */
export function CorpoDaGaveta({ children }: { children: ReactNode }) {
  return <div className="flex-1 overflow-y-auto p-5">{children}</div>
}

/** O rodapé de 64px da spec 01 — as ações, sempre visíveis. */
export function RodapeDaGaveta({ children }: { children: ReactNode }) {
  return (
    <footer className="flex min-h-[64px] shrink-0 flex-wrap items-center justify-end gap-2.5 border-t border-borda bg-superficie-suave px-5 py-3">
      {children}
    </footer>
  )
}
